// Creación de empresa a partir de los datos del onboarding. Extraído de
// `Onboarding.handleSubmit` para compartirlo con el alta enterprise
// (`EnterpriseCheckout`) sin duplicar la secuencia (profiles → webhook
// `analizar_nombre_y_logo` → rubro → marca default). Ver ADR.
//
// NO hace refreshPermissions ni navega: eso queda en cada caller (depende del
// contexto/UI). Devuelve `{ ok, companyId }` o `{ ok:false, error }`.

import { supabase } from './supabase';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { captureException } from './posthog';
import { ensureDefaultBrand } from './ensureDefaultBrand';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

const ANALIZAR_NOMBRE_Y_LOGO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/analizar_nombre_y_logo';

/**
 * Tope para la llamada de setup. Sin esto el fetch queda colgado hasta que lo corta
 * el navegador (minutos en mobile), y el usuario mira una pantalla trabada. Con el
 * tope, la falla es rápida y determinística — y arriba la recuperación puede
 * preguntarle a la base si el alta llegó igual.
 */
const SETUP_TIMEOUT_MS = 30_000;

/**
 * Falla de **transporte** del setup: red caída, timeout, 5xx o respuesta ilegible.
 * Es distinta de `{response:'error'}` — eso es un rechazo del backend CON motivo, y
 * ahí no hay nada que reintentar.
 *
 * Existe porque el `catch {}` de antes tiraba la única evidencia: el alta fallaba en
 * el front, la empresa quedaba creada en el back, y el mensaje genérico no dejaba
 * saber por qué (ADR-2026-08-03-alta-de-empresa-no-pierde-el-error).
 */
export class OnboardingSetupTransportError extends Error {
  constructor(
    message: string,
    /** Causa concreta, corta y mostrable (status + recorte del cuerpo, o el motivo del abort). */
    readonly detalle: string,
  ) {
    super(message);
    this.name = 'OnboardingSetupTransportError';
  }
}

export interface OnboardingSetupPayload {
  company_name: string;
  industry: string;
  user_id: string;
  name?: string;
  pais?: string;
  /** OwnerOnboarding arrastra campos legacy (address, logo_url…); ambos backs los ignoran. */
  [extra: string]: unknown;
}

export interface OnboardingSetupResponse {
  response: 'success' | 'error';
  description?: string;
}

// Setup del onboarding: crea la empresa (company_profiles) + linkea id_company.
// Flag ON → atlas-backend `/v1/onboarding/setup` con JWT: user_id NO viaja (lo
// deriva el backend) y el alta es idempotente (doble submit no duplica empresa).
// Los 400/404 traen `{response:'error', description}` — mismo shape que n8n.
// Flag OFF → webhook n8n `analizar_nombre_y_logo` byte-idéntico.
//
// Devuelve la respuesta del backend SÓLO si tiene el contrato esperado. Cualquier
// otra cosa (fetch que rechaza, timeout, 5xx, HTML de un gateway, cuerpo vacío)
// sale como `OnboardingSetupTransportError` con la causa adentro: antes todo eso
// se colapsaba en un `catch {}` y quedaba indistinguible.
export async function postOnboardingSetup(
  payload: OnboardingSetupPayload,
): Promise<OnboardingSetupResponse> {
  const ctrl = new AbortController();
  // El timer se limpia recién en el `finally` de AFUERA, o sea después de leer el
  // cuerpo. Limpiarlo apenas llegan los headers dejaba la lectura del body sin tope:
  // un backend (o un intermediario) que manda los headers y después se cuelga
  // transmitiendo dejaba el `await response.text()` colgado para siempre y la pantalla
  // trabada en "Preparando tu cuenta…" — justo el escenario que este timeout existe
  // para cortar. El abort sí interrumpe el stream del cuerpo, así que un solo timer
  // cubre las dos etapas.
  const timer = window.setTimeout(() => ctrl.abort(), SETUP_TIMEOUT_MS);
  const porTimeout = () => `sin respuesta en ${SETUP_TIMEOUT_MS / 1000}s`;

  try {
    let response: Response;
    try {
      if (USE_ATLAS_BACKEND) {
        const { user_id: _ignored, ...body } = payload;
        response = await atlasBackendFetch('/v1/onboarding/setup', body, {
          signal: ctrl.signal,
        });
      } else {
        response = await fetch(ANALIZAR_NOMBRE_Y_LOGO_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
      }
    } catch (err) {
      throw new OnboardingSetupTransportError(
        'La llamada de setup no llegó a destino',
        ctrl.signal.aborted ? porTimeout() : `red: ${String(err)}`,
      );
    }

    // `text()` y no `json()`: un 502 con HTML o un cuerpo vacío hacían explotar el
    // parseo y el motivo real (el status) se perdía. Así el status y el recorte del
    // cuerpo viajan dentro del error.
    let texto: string;
    try {
      texto = await response.text();
    } catch (err) {
      throw new OnboardingSetupTransportError(
        'La respuesta de setup se cortó a mitad',
        ctrl.signal.aborted
          ? `HTTP ${response.status} · ${porTimeout()} leyendo el cuerpo`
          : `HTTP ${response.status} · cuerpo ilegible: ${String(err)}`,
      );
    }

    let data: unknown = null;
    try {
      data = texto ? JSON.parse(texto) : null;
    } catch {
      data = null;
    }

    const resp = (data as OnboardingSetupResponse | null)?.response;
    if (resp === 'success' || resp === 'error') return data as OnboardingSetupResponse;

    throw new OnboardingSetupTransportError(
      'El backend respondió algo que no es el contrato de setup',
      `HTTP ${response.status} · ${texto.slice(0, 200) || '(cuerpo vacío)'}`,
    );
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * `profiles.id_company` del usuario, o null.
 *
 * Distingue **"no hay empresa"** (respuesta buena, `null`) de **"no pude preguntar"**
 * (error de PostgREST o de red): sólo lo segundo reintenta. La diferencia importa
 * porque el `null` se usa como respuesta de negocio en el rescate del alta —ahí un
 * negativo rápido es lo correcto— pero un `null` que en realidad era un blip de red
 * hace que el alta se dé por buena sin id: sin marca default, sin atribución y, aguas
 * arriba, sin `tipos_contenido`, todo en silencio.
 */
async function idCompanyDe(userId: string): Promise<string | null> {
  for (let intento = 1; intento <= 2; intento++) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', userId)
        .single();
      if (!error) {
        return (data as { id_company?: string | null } | null)?.id_company ?? null;
      }
    } catch {
      /* cae al reintento */
    }
  }
  return null;
}

export interface CreateCompanyInput {
  userId: string;
  name: string;
  pais: string;
  companyName: string;
  industry: string;
  /** session_id de atribución que la landing pasó en `?sid`. Best-effort: se
   *  persiste en `company_profiles.attribution_session_id` para cerrar el linkeo
   *  empresa ⟷ sesión anónima que originó el alta. */
  sessionId?: string | null;
}

export interface CreateCompanyResult {
  ok: boolean;
  companyId?: string | null;
  error?: string;
}

export async function createCompanyForUser(
  input: CreateCompanyInput,
): Promise<CreateCompanyResult> {
  const name = input.name.trim();
  const industry = input.industry.trim();
  const companyName = input.companyName.trim();

  // 1) Guardar name + pais en profiles.
  const { error: profileError } = await supabase
    .from('profiles')
    .update({ name, pais: input.pais })
    .eq('id', input.userId);
  if (profileError) return { ok: false, error: profileError.message };

  // 2) Backend que CREA la empresa (company_profiles) + setea id_company.
  //
  // Una falla de transporte NO significa que el alta no haya ocurrido: el backend
  // commitea la transacción ANTES de responder, así que una respuesta perdida en el
  // camino deja la empresa creada igual. Eso fue exactamente lo que pasó el 03/08 —
  // las dos altas de prueba contra producción mostraron "No se pudo crear la
  // empresa" con la empresa ya creada, y encima se saltearon el rol y los tipos de
  // contenido, que se guardan recién después del `ok`.
  // Por eso, ante un fallo de transporte: primero PREGUNTAR si llegó, y sólo
  // después reintentar. Ver ADR-2026-08-03-alta-de-empresa-no-pierde-el-error.
  let data: OnboardingSetupResponse | null = null;
  let ultimoDetalle = '';
  /** Id que devolvió el rescate, para no volver a preguntarlo abajo (ver paso 3). */
  let companyIdRescatado: string | null = null;
  for (let intento = 1; intento <= 2 && !data; intento++) {
    try {
      data = await postOnboardingSetup({
        company_name: companyName,
        industry,
        user_id: input.userId,
        name,
        pais: input.pais,
      });
    } catch (err) {
      ultimoDetalle =
        err instanceof OnboardingSetupTransportError ? err.detalle : String(err);
      // Se reporta SIEMPRE, también cuando la recuperación de abajo salva el alta:
      // un alta que sólo funciona por el rescate es un problema que hay que ver.
      captureException(err, { flujo: 'onboarding_setup', intento, detalle: ultimoDetalle });

      // ¿Llegó igual? Si el perfil ya tiene empresa, el alta está hecha. El id se
      // GUARDA: abajo se reusa en vez de releerlo (ver paso 3).
      companyIdRescatado = await idCompanyDe(input.userId);
      if (companyIdRescatado) {
        data = { response: 'success' };
        break;
      }
      // Reintento SÓLO contra atlas-backend: su `/v1/onboarding/setup` es idempotente
      // (toma `FOR UPDATE` del perfil y devuelve success si ya hay empresa). El webhook
      // de n8n no lo es — reintentar ahí crearía una segunda empresa.
      if (!USE_ATLAS_BACKEND) break;
    }
  }
  if (!data) {
    return {
      ok: false,
      // El detalle va en el mensaje a propósito: quien reporta el problema está en un
      // teléfono, sin consola, y "Reintentá" a secas no dejaba diagnosticar nada.
      error: `No se pudo crear la empresa. Revisá tu conexión y reintentá.${
        ultimoDetalle ? ` (${ultimoDetalle})` : ''
      }`,
    };
  }
  if (data.response === 'error') {
    return { ok: false, error: data.description || 'Error al crear la empresa.' };
  }

  // 3) Rubro + marca default (best-effort, no bloquea el alta).
  //
  // Si el alta se salvó por el rescate, el id YA lo sabemos: releerlo era tirar un
  // dato bueno y volver a jugarse a que PostgREST conteste. Un blip en esa segunda
  // lectura devolvía null y se salteaba en silencio rubro, atribución, marca default
  // y (aguas arriba) `tipos_contenido` — con la empresa creada y `ok: true`.
  let companyId: string | null = companyIdRescatado;
  try {
    if (!companyId) companyId = await idCompanyDe(input.userId);
    if (companyId) {
      // Junto con el rubro persistimos el session_id de atribución (si vino de la
      // landing). Solo se setea si llegó — no pisa con null.
      const companyUpdate: Record<string, unknown> = { rubro: industry };
      if (input.sessionId) companyUpdate.attribution_session_id = input.sessionId;
      await supabase
        .from('company_profiles')
        .update(companyUpdate)
        .eq('id_company', companyId);
      mergeLastCrearAnuncioState({ rubro: industry });
      await ensureDefaultBrand(companyId);
    } else {
      // El alta dio success pero no hay id que resolver: el perfil todavía no lo tiene
      // (replicación) o las dos lecturas fallaron. La empresa existe, pero queda sin
      // marca default, sin atribución y sin `tipos_contenido`. Es un estado degradado
      // que el usuario no ve — que al menos no sea invisible para nosotros.
      captureException(new Error('setup ok pero id_company no resoluble'), {
        flujo: 'onboarding_setup',
        rescatado: companyIdRescatado !== null,
      });
    }
  } catch {
    /* best-effort */
  }

  return { ok: true, companyId };
}
